import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Loader2, Users } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, withNext } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader } from "@/components/Loader";
import { ROLE_LABEL } from "@/lib/perms";

export default function JoinPage({ mode }) {
  const { token } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [requested, setRequested] = useState(false);
  const base = mode === "invite" ? `/invites/token/${token}` : `/join/${token}`;
  useEffect(() => { api.get(base).then((r) => setInfo(r.data)).catch(() => setInfo(false)); }, [base]);
  const accept = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(mode === "invite" ? `${base}/accept` : base);
      if (data.requested) { setRequested(true); setBusy(false); return; }
      toast.success("Welcome to the world"); nav(`/worlds/${data.world_id}`);
    }
    catch (e) { toast.error(formatApiError(e)); setBusy(false); }
  };
  if (info === null || user === null) return <Loader />;
  const here = mode === "invite" ? `/invite/${token}` : `/join/${token}`;
  const dead = !info || (mode === "invite" && info.status !== "pending");
  return (
    <div className="relative z-10 min-h-screen flex items-center justify-center px-6" data-testid="join-page">
      <div className="max-w-lg w-full text-center space-y-6 rise">
        <Users className="w-10 h-10 text-amber-400 mx-auto" />
        {requested ? (
          <><h1 className="font-display text-4xl text-amber-50" data-testid="join-requested">Request sent</h1>
            <p className="text-slate-400">The owner of {info.world_name} will look at your request. We'll let you know in your notifications once you're in.</p>
            <Link to="/worlds" className="inline-block rounded-full border border-line px-6 py-2.5 text-slate-200 hover:border-amber-500/40" data-testid="join-back-btn">Back to my worlds</Link></>
        ) : dead ? (
          <><h1 className="font-display text-4xl text-amber-50" data-testid="join-invalid">This invite isn't valid</h1>
            <p className="text-slate-400">{info?.status === "expired" ? "It has expired." : info?.status === "accepted" ? "It has already been accepted." : "It may have been cancelled, switched off or reset."} Ask the world owner for a new one.</p></>
        ) : (
          <>
            <p className="font-code text-xs uppercase tracking-[0.3em] text-amber-500/80">{mode === "invite" ? `${info.inviter_name} invited you` : "You're invited"}</p>
            <h1 className="font-display text-4xl sm:text-5xl text-amber-50">Join {info.world_name}</h1>
            <p className="text-slate-400">You'll join as {ROLE_LABEL[info.role] === "Admin" || ROLE_LABEL[info.role] === "Editor" ? "an" : "a"} <span className="text-amber-200">{ROLE_LABEL[info.role]}</span>.{mode === "invite" && ` Use ${info.email} to accept.`}{info.approval && " The owner approves each request first."}</p>
            {user ? (
              <button onClick={accept} disabled={busy} className="inline-flex items-center gap-2 rounded-full bg-amber-500 hover:bg-amber-400 text-onaccent font-semibold px-8 h-11" data-testid="join-accept-btn">{busy && <Loader2 className="w-4 h-4 animate-spin" />} {info.approval ? "Ask to join" : "Accept invite"}</button>
            ) : (
              <div className="flex flex-wrap justify-center gap-3">
                <Link to={withNext("/register", here)} className="rounded-full bg-amber-500 text-onaccent font-semibold px-6 py-2.5" data-testid="join-register-btn">Create a free account</Link>
                <Link to={withNext("/login", here)} className="rounded-full border border-line px-6 py-2.5 text-slate-200" data-testid="join-login-btn">Sign in</Link>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
